import jsPDF from "jspdf";
import "jspdf-autotable";
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const buildFiltrosTexto = ({ fechaDesde, fechaHasta, cliente, grupo, destino, canal, orden }) => {
  const filtros = [];

  if (fechaDesde || fechaHasta) {
    filtros.push(`Rango: ${fechaDesde || "N/A"} - ${fechaHasta || "N/A"}`);
  }
  if (cliente) filtros.push(`Cliente: ${cliente}`);
  if (grupo) filtros.push(`Grupo: ${grupo}`);
  if (destino) filtros.push(`Destino: ${destino}`);
  if (canal) filtros.push(`Canal: ${canal}`);
  if (orden) filtros.push(`Orden: ${orden}`);

  return filtros.length ? filtros : ["Filtros: Todos"];
};

export const generarPdfClientePaqueteDetalle = ({ rows = [], filtros = {}, usuario = "Usuario", logoBase64 = null, deliveryContext = null }) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
  };

  const totalPrecioOriginal = rows.reduce((sum, row) => sum + (Number(row?.precio) || 0), 0);
  const totalPrecioActualizado = rows.reduce(
    (sum, row) => sum + (Number(row?.precioActualizado) || 0),
    0
  );
  const totalAbonado = rows.reduce((sum, row) => sum + (Number(row?.pagado) || 0), 0);
  const totalSaldo = rows.reduce((sum, row) => sum + (Number(row?.saldo) || 0), 0);

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let yPosition = 36;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...PAL.primary);
  doc.text("REPORTE CLIENTE GRUPO", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.inkLight);
  doc.text(
    `Fecha de generacion: ${new Date().toLocaleString("es-AR")} | Usuario: ${usuario}`,
    pageWidth - margin,
    yPosition,
    { align: "right" }
  );
  yPosition += 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...PAL.ink);
  doc.text("Filtros aplicados:", margin, yPosition);
  yPosition += 14;

  doc.setDrawColor(...PAL.primary);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 12;

  const filtrosTexto = buildFiltrosTexto(filtros);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.muted);
  doc.text(filtrosTexto.join(" | "), margin, yPosition, {
    maxWidth: pageWidth - margin * 2,
  });
  yPosition += 24;

  doc.setFillColor(...PAL.bgSoft);
  doc.roundedRect(margin, yPosition - 13, 760, 20, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.ink);
  doc.text(
    `Registros: ${rows.length} | Precio Original: ${formatCurrency(totalPrecioOriginal)} | Precio Actualizado: ${formatCurrency(totalPrecioActualizado)} | Abonado: ${formatCurrency(totalAbonado)} | Saldo: ${formatCurrency(totalSaldo)}`,
    margin + 8,
    yPosition
  );
  yPosition += 24;

  const head = [
    "Fecha Viaje",
    "Cliente",
    "Grupo",
    "Destino",
    "Precio Original",
    "Precio Actualizado",
    "Abonado",
    "Saldo",
    "Liberado",
  ];

  const body = rows.map((row) => [
    row?.fechaViaje || "Sin fecha",
    row?.cliente || "",
    row?.codigo || "",
    row?.ubicacion || "",
    formatCurrency(row?.precio),
    formatCurrency(row?.precioActualizado),
    formatCurrency(row?.pagado),
    formatCurrency(row?.saldo),
    row?.liberado ? "Si" : "No",
  ]);

  doc.autoTable({
    startY: yPosition,
    head: [head],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { cellWidth: 70, halign: "center" },
      1: { cellWidth: 145 },
      2: { cellWidth: 105 },
      3: { cellWidth: 110 },
      4: { cellWidth: 78, halign: "right" },
      5: { cellWidth: 88, halign: "right" },
      6: { cellWidth: 70, halign: "right" },
      7: { cellWidth: 70, halign: "right" },
      8: { cellWidth: 55, halign: "center" },
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...PAL.inkLight);
      const pageText = `Pagina ${data.pageNumber} de ${doc.internal.pages.length - 1}`;
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" });
    },
  });

  const fileName = `cliente_grupo_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.setProperties({ title: fileName, subject: "Reporte Cliente Grupo", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true;
};
